import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { removeTrapFocus, trapFocus } from '@theme/focus';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Mobile region and language modal for the header.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} countryInput
 *
 * @extends {DialogComponent}
 */
class RegionLanguageModal extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'countryInput', 'panel', 'handle'];

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {string} */
  #selectedCountryCode = '';

  /** @type {string} */
  #selectedLanguageCode = '';

  /** @type {{ pointerId: number, startY: number, currentY: number, threshold: number, handle: HTMLElement } | null} */
  #dragState = null;

  /** @type {number | null} */
  #dragResetTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncDialogAccessibility();
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.refs.handle?.addEventListener('pointerdown', this.startDrag);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.refs.handle?.removeEventListener('pointerdown', this.startDrag);
    this.#removeDragListeners();
    removeTrapFocus();
  }

  /**
   * Open the modal from the mobile trigger.
   * @param {Event} [event]
   */
  showDialog(event) {
    if (!isMobileBreakpoint()) return;

    this.#clearDragState();
    this.#syncDialogAccessibility();

    const trigger =
      event?.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : event?.target instanceof HTMLElement
          ? event.target.closest('button')
          : null;

    this.#trigger = trigger instanceof HTMLElement ? trigger : document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.#resetPendingState();
    super.showDialog();
  }

  /**
   * Close the modal and return focus to the trigger.
   */
  async closeDialog() {
    if (!this.refs.dialog?.open) return;

    this.#releaseDragCapture();
    this.#dragState = null;
    removeTrapFocus();
    await super.closeDialog();
    this.#clearDragState();
    this.#trigger?.focus();
  }

  /**
   * Apply the selected region immediately.
   * @param {string} countryCode
   * @param {Event} event
   */
  selectCountry = (countryCode, event) => {
    event.preventDefault();
    this.#selectedCountryCode = countryCode;
    this.#syncCountrySelection();
    this.#submitSelection();
  };

  /**
   * Apply the selected language immediately.
   * @param {Event} event
   */
  updateLanguage = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    this.#selectedLanguageCode = target.value;
    this.#syncLanguageSelection();
    this.#submitSelection();
  };

  /**
   * Apply the selected language immediately from the row list.
   * @param {string} languageCode
   * @param {Event} event
   */
  selectLanguage = (languageCode, event) => {
    event.preventDefault();
    this.#selectedLanguageCode = languageCode;
    this.#syncLanguageSelection();
    this.#submitSelection();
  };

  /**
   * Start dragging the drawer down from the handle.
   * @param {PointerEvent} event
   */
  startDrag = (event) => {
    if (!isMobileBreakpoint() || event.button !== 0) return;
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const panel = this.refs.panel;
    if (!(panel instanceof HTMLElement)) return;

    event.preventDefault();

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is best effort.
    }

    const panelHeight = panel.getBoundingClientRect().height;
    const threshold = Math.max(80, Math.min(120, panelHeight * 0.25));

    this.#dragState = {
      pointerId: event.pointerId,
      startY: event.clientY,
      currentY: event.clientY,
      threshold,
      handle: event.currentTarget,
    };

    panel.style.transition = 'none';
    panel.style.transform = 'translateY(0px)';
    panel.setAttribute('data-dragging', '');

    this.#removeDragListeners();
    document.addEventListener('pointermove', this.handleDragMove);
    document.addEventListener('pointerup', this.endDrag);
    document.addEventListener('pointercancel', this.cancelDrag);
  };

  /**
   * Update the drawer position while dragging.
   * @param {PointerEvent} event
   */
  handleDragMove = (event) => {
    const dragState = this.#dragState;
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    const panel = this.refs.panel;
    if (!(panel instanceof HTMLElement)) return;

    const delta = Math.max(0, event.clientY - dragState.startY);
    dragState.currentY = event.clientY;

    panel.style.transform = `translateY(${delta}px)`;
    if (delta > 0) {
      event.preventDefault();
    }
  };

  /**
   * Finish the drag gesture and either close or snap back.
   * @param {PointerEvent} event
   */
  endDrag = (event) => {
    const dragState = this.#dragState;
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    this.#removeDragListeners();
    this.#releaseDragCapture();

    const draggedDistance = Math.max(0, dragState.currentY - dragState.startY);
    const shouldClose = draggedDistance >= dragState.threshold;

    this.#dragState = null;

    if (shouldClose) {
      this.closeDialog();
      return;
    }

    this.#snapPanelBack();
  };

  /**
   * Cancel the drag gesture and restore the drawer.
   * @param {PointerEvent} event
   */
  cancelDrag = (event) => {
    const dragState = this.#dragState;
    if (!dragState || event.pointerId !== dragState.pointerId) return;

    this.#removeDragListeners();
    this.#releaseDragCapture();
    this.#dragState = null;
    this.#snapPanelBack();
  };

  /**
   * Trap focus while the dialog is open.
   */
  #handleDialogOpen = () => {
    trapFocus(this.refs.dialog);
  };

  /**
   * Sync dialog accessibility labels with the current title visibility.
   */
  #syncDialogAccessibility() {
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const hasTitleMarkup = Boolean(this.querySelector('#region-language-modal-heading'));

    if (hasTitleMarkup) {
      dialog.setAttribute('aria-labelledby', 'region-language-modal-heading');
      dialog.setAttribute('aria-describedby', 'region-language-modal-subtitle');
      dialog.removeAttribute('aria-label');
      return;
    }

    dialog.removeAttribute('aria-labelledby');
    dialog.removeAttribute('aria-describedby');
  }

  /**
   * Release the active pointer capture for the drag handle.
   */
  #releaseDragCapture() {
    const dragState = this.#dragState;
    if (!dragState) return;

    try {
      if (dragState.handle.hasPointerCapture(dragState.pointerId)) {
        dragState.handle.releasePointerCapture(dragState.pointerId);
      }
    } catch {
      // Ignore release errors when the pointer has already been cleared.
    }
  }

  /**
   * Remove document-level drag listeners.
   */
  #removeDragListeners() {
    document.removeEventListener('pointermove', this.handleDragMove);
    document.removeEventListener('pointerup', this.endDrag);
    document.removeEventListener('pointercancel', this.cancelDrag);
  }

  /**
   * Submit the localization form without closing the modal first.
   */
  async #submitSelection() {
    this.#applyPendingStateToForm();

    if (typeof this.refs.form.requestSubmit === 'function') {
      this.refs.form.requestSubmit();
    } else {
      this.refs.form.submit();
    }
  }

  /**
   * Clear drag-related styles immediately.
   */
  #clearDragState() {
    this.#dragState = null;
    this.#removeDragListeners();

    if (this.#dragResetTimer) {
      window.clearTimeout(this.#dragResetTimer);
      this.#dragResetTimer = null;
    }

    const panel = this.refs.panel;
    if (panel instanceof HTMLElement) {
      panel.removeAttribute('data-dragging');
      panel.style.removeProperty('transition');
      panel.style.removeProperty('transform');
    }
  }

  /**
   * Snap the drawer back to its open position.
   */
  #snapPanelBack() {
    const panel = this.refs.panel;
    if (!(panel instanceof HTMLElement)) return;

    if (this.#dragResetTimer) {
      window.clearTimeout(this.#dragResetTimer);
    }

    panel.style.transition = 'transform 180ms var(--animation-easing)';
    panel.style.transform = 'translateY(0px)';
    panel.removeAttribute('data-dragging');

    this.#dragResetTimer = window.setTimeout(() => {
      this.#dragResetTimer = null;
      this.#clearDragState();
    }, 220);
  }

  /**
   * Reset local state from the current locale before each open.
   */
  #resetPendingState() {
    const countryButtons = this.#getCountryButtons();
    const availableCountryCodes = countryButtons
      .map((button) => button.dataset.countryCode)
      .filter((value) => Boolean(value));

    const countryInput = this.refs.countryInput;
    const languageInput = this.refs.languageInput;
    const languageButtons = this.#getLanguageButtons();

    if (!(countryInput instanceof HTMLInputElement)) {
      return;
    }

    const currentCountryCode = this.dataset.currentCountry ?? countryInput.value;
    const currentLanguageCode = this.dataset.currentLanguage ?? languageInput?.value ?? '';
    const availableLanguageCodes =
      languageInput instanceof HTMLSelectElement
        ? Array.from(languageInput.options).map((option) => option.value)
        : languageButtons.map((button) => button.dataset.languageCode).filter((value) => Boolean(value));

    this.#selectedCountryCode =
      currentCountryCode && availableCountryCodes.includes(currentCountryCode)
        ? currentCountryCode
        : availableCountryCodes[0] ?? currentCountryCode;

    this.#selectedLanguageCode =
      currentLanguageCode && availableLanguageCodes.includes(currentLanguageCode)
        ? currentLanguageCode
        : availableLanguageCodes[0] ?? currentLanguageCode;

    this.#syncCountrySelection();
    this.#syncLanguageSelection();
  }

  /**
   * Keep the visual selection and hidden country input in sync.
   */
  #syncCountrySelection() {
    const countryInput = this.refs.countryInput;
    if (!(countryInput instanceof HTMLInputElement)) return;

    countryInput.value = this.#selectedCountryCode;

    for (const button of this.#getCountryButtons()) {
      const isSelected = button.dataset.countryCode === this.#selectedCountryCode;
      button.classList.toggle('region-language-modal__region-row--selected', isSelected);
      button.setAttribute('aria-checked', String(isSelected));
      if (isSelected) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Keep the visual selection and language form control in sync.
   */
  #syncLanguageSelection() {
    const languageInput = this.refs.languageInput;

    if (languageInput instanceof HTMLInputElement || languageInput instanceof HTMLSelectElement) {
      languageInput.value = this.#selectedLanguageCode;
    }

    for (const button of this.#getLanguageButtons()) {
      const isSelected = button.dataset.languageCode === this.#selectedLanguageCode;
      button.classList.toggle('region-language-modal__region-row--selected', isSelected);
      button.setAttribute('aria-checked', String(isSelected));
      if (isSelected) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Apply pending selections to the form controls before submit.
   */
  #applyPendingStateToForm() {
    const countryInput = this.refs.countryInput;
    const languageInput = this.refs.languageInput;

    if (countryInput instanceof HTMLInputElement) {
      countryInput.value = this.#selectedCountryCode;
    }

    if (languageInput instanceof HTMLInputElement || languageInput instanceof HTMLSelectElement) {
      languageInput.value = this.#selectedLanguageCode;
    }
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getCountryButtons() {
    return Array.from(this.querySelectorAll('[data-region-country-button]'));
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  #getLanguageButtons() {
    return Array.from(this.querySelectorAll('[data-region-language-button]'));
  }
}

if (!customElements.get('region-language-modal')) {
  customElements.define('region-language-modal', RegionLanguageModal);
}
